import { transparentize } from "polished";
import {
  createGlobalStyle,
  css,
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider
} from "styled-components";

import RobotoMonoFont from "../assets/fonts/Roboto_Mono/static/RobotoMono-Regular.ttf";
import SfProRoundedFont from "../assets/fonts/sfProRounded.ttf";
import { Colors } from "./types/styled";

export * from './components'

const MEDIA_WIDTHS = {
	upToExtraSmall: 500,
	upToSmall: 720,
	upToMedium: 960,
	upToLarge: 1280,
}

const mediaWidthTemplates: {
	[width in keyof typeof MEDIA_WIDTHS]: typeof css
} = Object.keys(MEDIA_WIDTHS).reduce((accumulator, size) => {
	;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
		@media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
			${css(a, b, c)}
		}
	`
	return accumulator
}, {}) as any

export function colors(): Colors {
	return {
		// base
		white: '#FFFFFF',
		black: '#000000',

		// text
		text1: '#FFFFFF',
		text2: '#C3C5CB',
		text3: '#6C7284',
		text4: '#565A69',
		text5: '#2C2F36',
		owner1: '#FF0000',
		owner2: '#FF4444',

		avg: `#eed202`,
		high: `#4BB543`,

		// Legacy
		// bg0: '#191B1F',
		// bg1: '#212429',

		// backgrounds / greys
		bg0: '#000000',
		bg1: '#0F0F0F',
		// bg2: '#2C2F36',
		bg2: '#1E1E1E',
		bg3: '#40444F',
		bg4: '#565A69',
		bg5: '#6C7284',
		bg6: '#1A2028',

		//primary colors
		primary1: '#2172E5',
		primary2: '#3680E7',
		primary3: '#4D8FEA',
		primary4: '#376bad70',
		primary5: '#153d6f70',

		// color text
		primaryText1: '#6da8ff',

		// secondary colors
		secondary1: '#ff007a',
		secondary2: '#F6DDE8',
		secondary3: '#FDEAF1',

		twitter: '#00acee',
		instagram: '#E1306C',
		discord: '#7289da',
	}
}

export function theme(): DefaultTheme {
	return {
		...colors(),

		//shadows
		shadow1: '#000',

		// media queries
		mediaWidth: mediaWidthTemplates,

		// css snippets
		flexColumnNoWrap: css`
			display: flex;
			flex-flow: column nowrap;
		`,
		flexRowNoWrap: css`
			display: flex;
			flex-flow: row nowrap;
		`,

		flexColumnCenter: css`
			display: flex;
			flex-flow: row nowrap;
			justofy-content: center;
		`,
	}
}

export default function ThemeProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const themeObject = theme()
	return (
		<StyledComponentsThemeProvider theme={themeObject}>
			{children}
		</StyledComponentsThemeProvider>
	)
}

export const FixedGlobalStyle = createGlobalStyle`
	@font-face {
		font-family: 'San Francisco';
		src: url(${SfProRoundedFont}) format('truetype');
		font-weight: bolder;
		font-style: normal;
	}
	@font-face {
		font-family: 'Roboto Mono';
		src: url(${RobotoMonoFont}) format('truetype');
		font-weight: normal;
		font-style: normal;
	}
    html, input, textarea, button {
		font-family: SFRounded;
    }
    @supports (font-variation-settings: normal) {
        html, input, textarea, button {
            font-family: 'San Francisco', sans-serif;
        }
    }

    html,
    body {
        margin: 0;
        padding: 0;
    }

    * {
        box-sizing: border-box;
    }

    button {
        user-select: none;
    }

    html {
        font-size: 16px;
        font-variant: none;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        font-feature-settings: 'ss01' on, 'ss02' on,  'cv01' on, 'cv03' on;
    }
`

export const ThemedGlobalStyle = createGlobalStyle`
    html {
        color: ${({ theme }) => theme.text1};
        background-color: ${({ theme }) => theme.bg1};
    }

    body {
        min-height: 100vh;
        background-position: 0 -30vh;
        background-repeat: no-repeeat;
        background-image: ${({ theme }) =>
					`radial-gradient(50% 50% at 50% 50%, ${transparentize(
						0.9,
						theme.primary1
					)} 0%, ${transparentize(1, theme.bg1)} 100%)`};
    }
`
